import { useMemo } from 'react';
import { cn } from '@/lib/utils';
import { MapPin as MapPinIcon } from '@phosphor-icons/react';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from '@/components/ui/tooltip';
import useCustomerLocations from 'src/api/accounts-receivable/customers/useCustomerLocations';

interface LocationPillProps {
  location?: {
    name?: string | null;
    address?: string | null;
  };
  locationId?: string;
  customerId?: string;
  onClick?: () => void;
}

export function LocationPill({
  location: providedLocation,
  locationId,
  customerId,
  onClick,
}: LocationPillProps) {
  const { data: locationsData, isLoading } = useCustomerLocations(
    customerId || '',
    !!locationId && !!customerId && !providedLocation,
  );

  const location = useMemo(() => {
    if (providedLocation) return providedLocation;
    if (!locationId || !locationsData?.locations) return null;
    return locationsData.locations.find((loc) => loc.id === locationId);
  }, [providedLocation, locationId, locationsData]);

  const tooltipContent = useMemo(() => {
    if (!location) return 'No address available';
    const parts = [];
    if (location.name) {
      parts.push(location.name);
    }
    if (location.address) {
      parts.push(location.address);
    }
    return parts.length > 0 ? parts.join('\n') : 'No address available';
  }, [location]);

  if (isLoading) {
    return (
      <Badge
        className={cn(
          'bg-purple-50 text-purple-600 border-purple-200',
          'hover:bg-purple-50',
        )}
      >
        Loading...
      </Badge>
    );
  }

  if (!location) {
    return null;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge
          className={cn(
            'bg-purple-50 text-purple-600 border-purple-200',
            'inline-flex items-center gap-1',
            'transition-all duration-200 ease-in-out',
            onClick
              ? [
                  'cursor-pointer',
                  'hover:bg-purple-100 hover:-translate-y-px hover:shadow-sm',
                  'active:translate-y-0 active:shadow-none',
                ]
              : 'hover:bg-purple-50',
          )}
          onClick={onClick}
        >
          <MapPinIcon className="size-4" />
          {location.name || location.address || 'Unnamed Location'}
        </Badge>
      </TooltipTrigger>
      <TooltipContent side="top" className="whitespace-pre-line max-w-[300px]">
        {tooltipContent}
      </TooltipContent>
    </Tooltip>
  );
}

export default LocationPill;
